import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { landOwnershipService } from '../land/land-ownership-service.js'
import type { SelectableLayerProvider } from '../selection/highlight-adapter.js'
import type { SelectableFeatureRef } from '../selection/selectable-feature-ref.js'

const log = logger.create('cotrex')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const PMTILES_URL = `${TILE_ORIGIN}/data/cotrex/cotrex.pmtiles`

const SOURCE_ID = 'cotrex-source'
const TRAIL_HIT_LAYER = 'cotrex-trails-hit'
const TRAIL_LINE_LAYER = 'cotrex-trails-line'
const TRAIL_HIGHLIGHT_LAYER = 'cotrex-trails-highlight'
const TRAIL_LABEL_LAYER = 'cotrex-trails-label'
const TRAILHEAD_LAYER = 'cotrex-trailheads-circle'

const DEFAULT_OPACITY = 0.8
const MIN_ZOOM = 6

const TRAIL_COLOR = '#0891b2'
const TRAILHEAD_COLOR = '#0d9488'

class CotrexLayerService implements SelectableLayerProvider {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _hoverCount = 0
  private _onTrailEnter = (): void => this.onHoverEnter()
  private _onTrailLeave = (): void => this.onHoverLeave()
  private _onTrailheadEnter = (): void => this.onHoverEnter()
  private _onTrailheadLeave = (): void => this.onHoverLeave()

  get enabled(): boolean {
    return this._enabled
  }

  get opacity(): number {
    return this._opacity
  }

  private pmtiles: PMTiles | null = null

  register(map: maplibregl.Map): void {
    this.map = map
    const protocol = landOwnershipService.getProtocol()
    if (protocol) {
      this.pmtiles = new PMTiles(PMTILES_URL)
      protocol.add(this.pmtiles)
    } else {
      log.warn('PMTiles protocol not available — COTREX tiles will not load')
    }
  }

  getPMTilesInstance(): PMTiles | null {
    return this.pmtiles
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'vector',
        url: `pmtiles://${PMTILES_URL}`,
        minzoom: MIN_ZOOM,
        maxzoom: 14,
      })
    }

    if (!this.map.getLayer(TRAIL_HIT_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAIL_HIT_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'cotrex_trails',
          minzoom: MIN_ZOOM,
          paint: {
            'line-color': 'transparent',
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 8, 14, 15],
          },
        },
        'cotrex'
      )
    }

    if (!this.map.getLayer(TRAIL_LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAIL_LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'cotrex_trails',
          minzoom: MIN_ZOOM,
          paint: {
            'line-color': TRAIL_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 10, 1.5, 14, 3],
            'line-opacity': this._opacity,
          },
        },
        'cotrex'
      )
    }

    if (!this.map.getLayer(TRAIL_HIGHLIGHT_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAIL_HIGHLIGHT_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'cotrex_trails',
          minzoom: MIN_ZOOM,
          filter: ['==', ['get', 'name'], ''],
          paint: {
            'line-color': '#facc15',
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 6, 14, 10],
            'line-opacity': 0.7,
          },
        },
        'cotrex'
      )
    }

    if (!this.map.getLayer(TRAILHEAD_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAILHEAD_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          'source-layer': 'cotrex_trailheads',
          minzoom: 10,
          paint: {
            'circle-color': TRAILHEAD_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 6],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
            'circle-opacity': this._opacity,
          },
        },
        'cotrex'
      )
    }

    if (!this.map.getLayer(TRAIL_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAIL_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'cotrex_trails',
          minzoom: 11,
          filter: ['has', 'name'],
          layout: {
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 13],
            'text-font': ['Open Sans Regular'],
            'symbol-placement': 'line',
            'text-max-angle': 30,
          },
          paint: {
            'text-color': TRAIL_COLOR,
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
            'text-opacity': this._opacity,
          },
        },
        'cotrex'
      )
    }

    this.map.on('mouseenter', TRAIL_HIT_LAYER, this._onTrailEnter)
    this.map.on('mouseleave', TRAIL_HIT_LAYER, this._onTrailLeave)
    this.map.on('mouseenter', TRAILHEAD_LAYER, this._onTrailheadEnter)
    this.map.on('mouseleave', TRAILHEAD_LAYER, this._onTrailheadLeave)
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this._hoverCount = 0

    this.map.off('mouseenter', TRAIL_HIT_LAYER, this._onTrailEnter)
    this.map.off('mouseleave', TRAIL_HIT_LAYER, this._onTrailLeave)
    this.map.off('mouseenter', TRAILHEAD_LAYER, this._onTrailheadEnter)
    this.map.off('mouseleave', TRAILHEAD_LAYER, this._onTrailheadLeave)

    if (this.map.getLayer(TRAIL_LABEL_LAYER)) this.map.removeLayer(TRAIL_LABEL_LAYER)
    if (this.map.getLayer(TRAILHEAD_LAYER)) this.map.removeLayer(TRAILHEAD_LAYER)
    if (this.map.getLayer(TRAIL_HIGHLIGHT_LAYER)) this.map.removeLayer(TRAIL_HIGHLIGHT_LAYER)
    if (this.map.getLayer(TRAIL_LINE_LAYER)) this.map.removeLayer(TRAIL_LINE_LAYER)
    if (this.map.getLayer(TRAIL_HIT_LAYER)) this.map.removeLayer(TRAIL_HIT_LAYER)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.enable()
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(TRAIL_LINE_LAYER)) {
      this.map.setPaintProperty(TRAIL_LINE_LAYER, 'line-opacity', this._opacity)
    }
    if (this.map?.getLayer(TRAILHEAD_LAYER)) {
      this.map.setPaintProperty(TRAILHEAD_LAYER, 'circle-opacity', this._opacity)
    }
    if (this.map?.getLayer(TRAIL_LABEL_LAYER)) {
      this.map.setPaintProperty(TRAIL_LABEL_LAYER, 'text-opacity', this._opacity)
    }
  }

  private onHoverEnter(): void {
    this._hoverCount++
    if (this._hoverCount === 1 && this.map) {
      this.map.getCanvas().style.cursor = 'pointer'
    }
  }

  private onHoverLeave(): void {
    this._hoverCount = Math.max(0, this._hoverCount - 1)
    if (this._hoverCount === 0 && this.map) {
      this.map.getCanvas().style.cursor = ''
    }
  }

  highlightTrails(refs: Array<{ name: string; placeId: number; featureId: string }>): void {
    if (!this.map?.getLayer(TRAIL_HIGHLIGHT_LAYER)) return
    if (refs.length === 0) {
      this.clearHighlight()
      return
    }
    const placeIds = refs.filter(r => r.placeId).map(r => r.placeId)
    const featureIds = refs.filter(r => !r.placeId && r.featureId).map(r => r.featureId)
    const nameOnly = refs.filter(r => !r.placeId && !r.featureId && r.name).map(r => r.name)
    const conditions: maplibregl.ExpressionSpecification[] = []
    if (placeIds.length > 0) {
      conditions.push([
        'in',
        ['get', 'place_id'],
        ['literal', placeIds],
      ] as maplibregl.ExpressionSpecification)
    }
    if (featureIds.length > 0) {
      conditions.push([
        'in',
        ['get', 'feature_id'],
        ['literal', featureIds],
      ] as maplibregl.ExpressionSpecification)
    }
    if (nameOnly.length > 0) {
      conditions.push([
        'in',
        ['get', 'name'],
        ['literal', nameOnly],
      ] as maplibregl.ExpressionSpecification)
    }
    const filter: maplibregl.ExpressionSpecification =
      conditions.length === 1 ? conditions[0] : ['any', ...conditions]
    this.map.setFilter(TRAIL_HIGHLIGHT_LAYER, filter)
  }

  clearHighlight(): void {
    if (!this.map?.getLayer(TRAIL_HIGHLIGHT_LAYER)) return
    this.map.setFilter(TRAIL_HIGHLIGHT_LAYER, ['==', ['get', 'name'], ''])
  }

  getSelectableLayerIds(): string[] {
    const layers: string[] = []
    if (this._enabled && this.map?.getLayer(TRAIL_HIT_LAYER)) layers.push(TRAIL_HIT_LAYER)
    if (this._enabled && this.map?.getLayer(TRAILHEAD_LAYER)) layers.push(TRAILHEAD_LAYER)
    return layers
  }

  featureToRef(hit: maplibregl.MapGeoJSONFeature): SelectableFeatureRef | null {
    const layerId = hit.layer.id
    const props = hit.properties

    if (layerId === TRAIL_HIT_LAYER || layerId === TRAIL_LINE_LAYER) {
      const name = (props.name as string | undefined) ?? ''
      const placeId = Number(props.place_id ?? 0)
      const featureId = String(props.feature_id ?? '')
      if (placeId || featureId || name) {
        return { source: 'cotrex-trail', name, placeId, featureId }
      }
    }

    if (layerId === TRAILHEAD_LAYER) {
      const name = props.name as string | undefined
      const geom = hit.geometry
      if (name && geom.type === 'Point') {
        return {
          source: 'cotrex-trailhead',
          name,
          coordinates: [geom.coordinates[1], geom.coordinates[0]],
        }
      }
    }

    return null
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map || !this._enabled) return []
    const layers: string[] = []
    if (this.map.getLayer(TRAIL_HIT_LAYER)) layers.push(TRAIL_HIT_LAYER)
    if (this.map.getLayer(TRAILHEAD_LAYER)) layers.push(TRAILHEAD_LAYER)
    if (layers.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers })
  }
}

export const cotrexLayerService = new CotrexLayerService()
